import { z } from "zod";

// D85 — engine-/domänen-NEUTRALES Form-Geometrie-Primitiv. EIN `Shape`, ZWEI Konsumenten-Rollen:
//   (a) pro-INSTANZ auf Region-Instanzen (das Area-Werkzeug zeichnet die Fläche, D75/D78);
//   (b) pro-PLACEABLE als `collision` auf dem PlaceableDescriptor (Hitbox, im Manifest, von Editor + Runtime gelesen).
// Die Punkte eines `polygon` sind LOKAL (anker-relativ; Anker = Instanz-`x/y` = bbox-Zentrum, wie rect/circle
// zentriert). Die reinen Helfer unten sind die EINE Implementierung von Bounds/Hit-Test/Vereinfachung —
// Editor (Viewport) und phaser-runtime konsumieren sie, statt die Geometrie doppelt zu tragen.

// Alle Geometriewerte ENDLICH (Codex-P2): `Infinity`/`NaN` würden Canvas-Mathe und Phaser-Bodies still
// korrumpieren — `.positive()` allein ließe Infinity passieren, nacktes `.number()` sogar NaN.
const finite = z.number().finite();
const finitePositive = finite.positive();

/** 2D-Punkt (eigen, da contracts NICHT von `@loumy/editor-tools` abhängen darf — strukturell kompatibel). */
export const ShapeVec2 = z.object({ x: finite, y: finite });
export type ShapeVec2 = z.infer<typeof ShapeVec2>;

/**
 * Eine Form. DISKRIMINANT `kind`: Rechteck (w×h), Kreis (Radius) oder Polygon (≥3 lokale Punkte). Anker-zentriert
 * per Default; rect/circle tragen einen OPTIONALEN Center-Offset `cx`/`cy` (anker-relativ, Default 0) — nötig, um
 * eine Collision-Box innerhalb eines Sprites zu VERSCHIEBEN (manuelle Anpassung, D85). Polygon trägt die Lage
 * bereits in seinen (anker-relativen) Punkten ⇒ kein Offset. Additiv-optional ⇒ Manifest bleibt schemaVersion 2.
 */
export const Shape = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("rect"), w: finitePositive, h: finitePositive, cx: finite.optional(), cy: finite.optional() }),
  z.object({ kind: z.literal("circle"), r: finitePositive, cx: finite.optional(), cy: finite.optional() }),
  z.object({ kind: z.literal("polygon"), points: z.array(ShapeVec2).min(3) }),
]);
export type Shape = z.infer<typeof Shape>;

/** Geometrischer Mittelpunkt (anker-relativ): rect/circle = optionaler `cx`/`cy`-Offset; polygon = bbox-Zentrum. */
export function shapeCenter(shape: Shape): { x: number; y: number } {
  if (shape.kind === "polygon") {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of shape.points) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  }
  return { x: shape.cx ?? 0, y: shape.cy ?? 0 };
}

/** Achsen-ausgerichtete Bounding-Box (Welt-Einheiten) — Editor-Größe & Runtime-Rechteck-Fallback. */
export function shapeBounds(shape: Shape): { w: number; h: number } {
  switch (shape.kind) {
    case "rect":
      return { w: shape.w, h: shape.h };
    case "circle":
      return { w: shape.r * 2, h: shape.r * 2 };
    case "polygon": {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const p of shape.points) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      }
      return { w: maxX - minX, h: maxY - minY };
    }
  }
}

/** Ray-Casting Punkt-in-Polygon (lokale Koordinaten). */
function pointInPolygon(pts: ShapeVec2[], x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const pi = pts[i]!;
    const pj = pts[j]!;
    const intersects = pi.y > y !== pj.y > y && x < ((pj.x - pi.x) * (y - pi.y)) / (pj.y - pi.y) + pi.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Trifft ein Punkt (lokal, anker-relativ) die Form? rect=AABB, circle=Radius (beide um `cx/cy` versetzt), polygon=Ray-Cast. */
export function pointInShape(shape: Shape, lx: number, ly: number): boolean {
  switch (shape.kind) {
    case "rect":
      return Math.abs(lx - (shape.cx ?? 0)) <= shape.w / 2 && Math.abs(ly - (shape.cy ?? 0)) <= shape.h / 2;
    case "circle": {
      const dx = lx - (shape.cx ?? 0);
      const dy = ly - (shape.cy ?? 0);
      return dx * dx + dy * dy <= shape.r * shape.r;
    }
    case "polygon":
      return pointInPolygon(shape.points, lx, ly);
  }
}

/** Senkrechter Abstand von `p` zur Strecke `a`–`b` (Douglas-Peucker-Maß). */
function perpDistance(p: ShapeVec2, a: ShapeVec2, b: ShapeVec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y); // a==b
  // |(p-a) × (b-a)| / |b-a|
  return Math.abs((p.x - a.x) * dy - (p.y - a.y) * dx) / Math.sqrt(len2);
}

/**
 * Douglas-Peucker: dünnt eine Punktfolge auf die signifikanten Stützpunkte aus (für die GROBE Auto-Kontur).
 * `tolerance` = max. erlaubter Abstand (Welt-Einheiten); größer = gröber.
 */
export function simplifyPolygon(points: ShapeVec2[], tolerance: number): ShapeVec2[] {
  if (points.length <= 2) return points.slice();
  const first = points[0]!;
  const last = points[points.length - 1]!;
  let maxDist = -1;
  let idx = -1;
  for (let i = 1; i < points.length - 1; i++) {
    const d = perpDistance(points[i]!, first, last);
    if (d > maxDist) {
      maxDist = d;
      idx = i;
    }
  }
  if (maxDist > tolerance && idx > 0) {
    const left = simplifyPolygon(points.slice(0, idx + 1), tolerance);
    const right = simplifyPolygon(points.slice(idx), tolerance);
    return [...left.slice(0, -1), ...right];
  }
  return [first, last];
}

/**
 * Welt-Punktfolge (z.B. aus dem Extrude-Controller) → anker-relatives Polygon. Anker = bbox-Zentrum
 * (Instanz-`x/y`), damit die Form wie rect/circle um den Anker zentriert ist. Fail-closed bei <3 Punkten.
 */
export function polygonShapeFromWorld(worldPoints: ShapeVec2[]): { anchor: { x: number; y: number }; shape: Shape } {
  if (worldPoints.length < 3) throw new Error(`polygonShapeFromWorld: ≥3 Punkte nötig (erhalten: ${worldPoints.length}).`);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of worldPoints) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const anchor = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  const points = worldPoints.map((p) => ({ x: p.x - anchor.x, y: p.y - anchor.y }));
  return { anchor, shape: { kind: "polygon", points } };
}
